import { Box, SxProps, Theme, Tooltip } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import InfoIcon from "@mui/icons-material/Info";
import { useCallback, useState, useEffect } from "react";

import { FilterComponent } from "./filter_component";
import { MinimalButton } from "./minimal_button_component";
import { PerspectiveViewFilter } from "../../entities/perspective_view_filter";

export function FilterBy({
  isLoading,
  excludedFilterColumnFilter,
  viewFilters,
  setViewFilters,
  columns,
  label,
  sx,
}: {
  isLoading: boolean;
  excludedFilterColumnFilter: string[];
  viewFilters: PerspectiveViewFilter[];
  setViewFilters: (newFilters: PerspectiveViewFilter[]) => void;
  columns: {
    name: string;
    caption: string;
    unique_values: string[];
  }[];
  label?: string;
  sx?: SxProps<Theme>;
}) {
  // Ensure viewFilters is always an array
  const safeViewFilters = Array.isArray(viewFilters) ? viewFilters : [];

  const userFilters = safeViewFilters.filter(
    (filter) => !excludedFilterColumnFilter.includes(filter[0])
  );
  const systemFilters = safeViewFilters.filter((filter) =>
    excludedFilterColumnFilter.includes(filter[0])
  );
  // tempViewFilters is used to store the filters that are currently being edited
  const [tempUserViewFilters, setTempUserViewFilters] =
    useState<PerspectiveViewFilter[]>(userFilters);

  // Sync tempUserViewFilters when viewFilters prop changes (e.g., from URL changes)
  useEffect(() => {
    const safeViewFilters = Array.isArray(viewFilters) ? viewFilters : [];
    const newUserFilters = safeViewFilters.filter(
      (filter) => !excludedFilterColumnFilter.includes(filter[0])
    );
    setTempUserViewFilters(newUserFilters);
  }, [viewFilters, excludedFilterColumnFilter]);

  const handleSave = useCallback(
    (newFilters?: PerspectiveViewFilter[]) => {
      if (newFilters !== undefined) {
        setViewFilters([...systemFilters, ...newFilters]);
      } else if (tempUserViewFilters.length) {
        setViewFilters([...systemFilters, ...tempUserViewFilters]);
      }
    },
    [setViewFilters, tempUserViewFilters, systemFilters]
  );

  return (
    <Box sx={sx}>
      <Box
        sx={{
          fontSize: 11,
          display: "flex",
          alignItems: "center",
        }}
      >
        {label ?? "Filter by"}{" "}
        <Tooltip title="You can filter by multiple values in same filter by selecting them in the dropdown. Filters are additive, not conditional.">
          <InfoIcon
            fontSize="small"
            sx={{
              width: "16px",
              height: "16px",
              color: "rgba(0, 0, 0, 0.3)",
              ml: 0.5,
            }}
          />
        </Tooltip>
      </Box>

      <Box sx={{ display: "flex", flexWrap: "wrap" }}>
        {!isLoading && tempUserViewFilters.length > 0 && (
          <>
            {tempUserViewFilters.map((f: PerspectiveViewFilter, i: number) => (
              <FilterComponent
                key={`${f[0]}-${f[1]}-${f[2]}-${i}`}
                // the following is for deciding if the filter is "saved" or not,
                // (applied to the table yet or not), so it's the non-temp filters
                viewFilters={viewFilters}
                viewFilter={f}
                filterIndex={i}
                handleChange={(
                  filter: PerspectiveViewFilter,
                  filterIndex: number,
                  immediateSave?: boolean
                ) => {
                  const newFilters = [...tempUserViewFilters];
                  newFilters[filterIndex] = filter;

                  if (immediateSave) {
                    handleSave(newFilters);
                  }
                  setTempUserViewFilters(newFilters);
                }}
                handleSave={handleSave}
                handleDelete={async (
                  filter: [string, string, string[] | string]
                ) => {
                  const newFilters = tempUserViewFilters.filter(
                    (f, index) => f[0] !== filter[0]
                  );
                  handleSave(newFilters);
                  setTempUserViewFilters(newFilters);
                }}
                columns={columns}
              />
            ))}
          </>
        )}

        <MinimalButton
          isLoading={isLoading}
          onClick={() => {
            const newTempUserViewFilters = [...tempUserViewFilters];
            newTempUserViewFilters.push([columns[0].name, "in", []]);
            setTempUserViewFilters(newTempUserViewFilters);
          }}
        >
          <AddIcon
            fontSize="small"
            sx={{
              width: "15px",
              color: isLoading ? "action.disabled" : "unset",
            }}
          />
        </MinimalButton>
      </Box>
    </Box>
  );
}
